import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

export interface SatirDugmesiProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

/**
 * SATIR DUGMESI — kart gorunumlu, tiklanabilir liste satiri.
 *
 * ================================================================
 * KART DEGIL, DUGME
 * ================================================================
 * Gorunumu kartla ayni ama `Kart` bileseni bunu KARSILAYAMAZ: `Kart`
 * bir `<section>`/`<li>` ciziyor, buradaki oge ise `<button>` olmak
 * ZORUNDA. Tiklanabilir bir kutuyu `div`e `onClick` takarak yapmak
 * klavyeyle ulasilamaz hale getirir ve ekran okuyucu onu bir eylem
 * olarak duyurmaz.
 *
 * Bu yuzden ayri bir bilesen: ayni gorsel dil, farkli anlam.
 *
 * ================================================================
 * ODAK HALKASI BURADA ACIKCA YAZILI
 * ================================================================
 * `tokens.css` genel bir `:focus-visible` kurali tasiyor ama bu oge
 * KART gorunumunde ve genel kural onun kose yaricapini bilmiyor.
 * Halkanin kartin kosesini takip etmesi icin burada tekrarlaniyor —
 * tekrar degil, ozellestirme.
 *
 * ================================================================
 * 44px DOKUNMA HEDEFI SABIT
 * ================================================================
 * `min-h-dokunma` her durumda duruyor. Satir tek satirlik bir metin
 * tasisa bile: telefonda bu satirlara parmakla basiliyor ve
 * 03-kod-standartlari.md bu olcuyu pazarlik disi tutuyor.
 */
export function SatirDugmesi({ children, className, ...rest }: SatirDugmesiProps) {
  return (
    <button
      {...rest}
      // HTML'de form icindeki `<button>` varsayilani SUBMIT.
      type={rest.type ?? "button"}
      className={cn(
        "min-h-dokunma rounded-kart border-notr-200 flex w-full items-center gap-3 border",
        "bg-notr-0 px-4 py-3 text-left",
        /*
          IKI YAZIM DA v3 SOZDIZIMIYDI VE IKISI DE SESSIZCE OLUYDU:
          `shadow-[--shadow-kart]` -> `--tw-shadow: --shadow-kart`
          `duration-[--sure-hizli]` -> `transition-duration: --sure-hizli`
          Ikisi de gecersiz deger; golge hic cizilmiyordu, gecis de
          HIC olmuyordu — gecersiz bir bildirim yok sayilmiyor,
          ozelligin BASLANGIC degerine dusuruluyor ve
          `transition-duration`in baslangici 0s (olculdu 04.09.2026).
          v4'te
          dogru yazim `(--degisken)` ya da — belirtec `@theme` icinde
          adlandirilmissa — dogrudan `shadow-kart`.
        */
        "shadow-kart transition-[background-color,box-shadow,border-color,translate]",
        "ease-cikis duration-(--sure-hizli)",
        /*
          UZERINE GELINCE BIR KADEME YUKSELIYOR. Belirtec olcegi bunu
          zaten adlandiriyor: `kart` duran yuzey, `yukselen` ISARET
          EDILEN yuzey (hover, secili satir). Satir tiklanabilir bir
          oge ve yukselmesi "bu tiklanir" demenin renksiz yolu.
        */
        "hover:border-notr-300 hover:bg-notr-50 hover:shadow-yukselen",
        // Basildiginda ASAGI: dinlenmede duz durdugu icin kalkacak yeri yok.
        "active:shadow-kart active:translate-y-px",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-marka-600",
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface SatirMetniProps {
  baslik: ReactNode;
  altBaslik?: ReactNode;
  children?: ReactNode;
}

/**
 * Satirin iki satirlik metin blogu.
 *
 * ================================================================
 * KIRPMA `min-w-0` OLMADAN CALISMIYOR
 * ================================================================
 * Esnek bir kutunun icindeki metin, kutu `min-w-0` almadan
 * KIRPILMIYOR: tarayici ogeyi icerigi kadar genisletiyor ve satir
 * tasarak yanindaki rozeti ekran disina itiyor.
 *
 * Bu, her iki ekranda da elle dogru yazilmisti — ama elle yazilan
 * her yerde biri unutabilirdi ve o zaman ariza yalnizca UZUN ISIMLI
 * bir ogrencide gorunurdu.
 */
export function SatirMetni({ baslik, altBaslik, children }: SatirMetniProps) {
  return (
    <span className="min-w-0 flex-1">
      <span className="text-notr-900 block truncate text-sm font-medium">{baslik}</span>
      {altBaslik && <span className="text-notr-600 block truncate text-sm">{altBaslik}</span>}
      {children}
    </span>
  );
}
